import { Checkbox, Input, Radio, SimpleGrid, Stack, Text } from '@mantine/core'
import type { Patient } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import * as patients from '@seen/core/models/patients'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { SDRStatus } from '@seen/core/models/tasks/sdr/utils'
import type { JSX } from 'react'
import { CodeableConceptsDisplay } from '#src/components/display/CodeableConceptsDisplay.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { adjustDateForSDRDeadlineAvoidingWeekends } from '#src/components/sdr/create/utils.ts'
import { useFhirSearchOne } from '#src/hooks/useFhirSearchOne.ts'

function getEffectuationDateLabel(sdr: SDR): string {
  return sdr.timeRemaining.text === 'Achieved'
    ? 'Effectuated Date'
    : 'Estimated Effectuation Date'
}

type Props = {
  sdr: SDR
  patient: Patient
}

export function SDRRequestDetails({ sdr, patient }: Props): JSX.Element {
  invariant(sdr.id, 'SDR ID is required')
  invariant(sdr.createdAt, 'SDR createdAt is required')

  const sdrStatus = sdr.status

  const title = sdr.title

  const requesterQuery = useFhirSearchOne('RelatedPerson', {
    _id: sdr.relatedPersonRequester?.reference,
  })
  const requester = sdr.relatedPersonRequester
    ? requesterQuery.query.data
    : patient
  const requesterRelationship =
    requester?.resourceType === 'RelatedPerson'
      ? requester.relationship
      : undefined

  const phoneNumber = patients.getPrimaryPhoneNumber(requester)
  const email = patients.getPrimaryEmail(requester)
  const address = patients.homeAddressOrFallback(requester)

  const reviewType = sdr.reviewType

  const receiptDate = sdr.receiptDate
  let reviewDueDate: string | undefined
  let reviewLabel: string
  if (sdrStatus === SDRStatus.REQUESTED) {
    reviewLabel = 'Needs to be reviewed by'
    reviewDueDate =
      sdr.decisionNotificationDate != null
        ? sdr.decisionNotificationDate.toISOString()
        : receiptDate != null
          ? adjustDateForSDRDeadlineAvoidingWeekends(receiptDate, 'IDTReview')
          : undefined
  } else {
    reviewLabel = 'Reviewed on'
    reviewDueDate = sdr.reviewDate
  }

  let decisionDueDate: string | undefined
  let decisionLabel: string
  if (sdr.idtDisposition) {
    decisionLabel = 'Decision made on'
    decisionDueDate = sdr.idtDisposition.dispositionDate.toISOString()
  } else {
    decisionLabel =
      sdrStatus === SDRStatus.REQUESTED
        ? 'IDT decision due by'
        : 'Decision due by'
    decisionDueDate =
      sdrStatus !== SDRStatus.REQUESTED && sdr.decisionNotificationDate != null
        ? sdr.decisionNotificationDate.toISOString()
        : reviewDueDate != null
          ? adjustDateForSDRDeadlineAvoidingWeekends(
              reviewDueDate,
              'DecisionNotification'
            )
          : undefined
  }
  const effectuationDate =
    decisionDueDate != null
      ? adjustDateForSDRDeadlineAvoidingWeekends(
          decisionDueDate,
          'Effectuation'
        )
      : undefined

  return (
    <Stack>
      <Text className="text-lg font-semibold">Request Details</Text>

      <SimpleGrid cols={{ base: 1, sm: 2 }} className="items-start">
        <Input.Wrapper label="Title" className="grow">
          <Text>{title}</Text>
        </Input.Wrapper>

        <Input.Wrapper label="Category" className="grow">
          <Text>{sdr.category}</Text>
        </Input.Wrapper>
      </SimpleGrid>

      <Input.Wrapper
        label="Description"
        description="Detailed description of the request, include the reasons why it is being requested"
      >
        <Text>{sdr.description}</Text>
      </Input.Wrapper>

      <Checkbox
        checked={true}
        label="Description read back to requestor for accuracy"
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Input.Wrapper label="Receipt Method">
          <Text>{sdr.receiptMethod}</Text>
        </Input.Wrapper>

        <Input.Wrapper label="Requested By">
          <Text>{referenceDisplay(sdr.requester)}</Text>
        </Input.Wrapper>

        <Input.Wrapper label="Relationship">
          <Text>
            {requesterRelationship ? (
              <CodeableConceptsDisplay
                codeableConcepts={requesterRelationship}
              />
            ) : (
              'Participant (self)'
            )}
          </Text>
        </Input.Wrapper>

        {/* phone */}
        <Input.Wrapper label="Phone Number">
          <Text>{phoneNumber}</Text>
        </Input.Wrapper>

        {/* email */}
        <Input.Wrapper label="Email">
          <Text>{email}</Text>
        </Input.Wrapper>

        {/* all address fields */}
        {address != null && (
          <>
            <Input.Wrapper label="Address">
              <Text>{address?.line?.join(' ')}</Text>
            </Input.Wrapper>

            <Input.Wrapper label="City">
              <Text>{address?.city}</Text>
            </Input.Wrapper>

            <Input.Wrapper label="State">
              <Text>{address?.state}</Text>
            </Input.Wrapper>

            <Input.Wrapper label="Postal Code">
              <Text>{address?.postalCode}</Text>
            </Input.Wrapper>

            <Input.Wrapper label="Country">
              {/* TODO: Make sure country is properly populated after SRR */}
              <Text>{address?.country ?? 'USA'}</Text>
            </Input.Wrapper>
          </>
        )}

        {/* <Input.Wrapper label="Attachments">
          <Text>TODO implement documents</Text>
        </Input.Wrapper> */}
      </SimpleGrid>

      {/* review information */}
      {/* direct approval or IDT group review */}
      <Input.Wrapper label="Who is reviewing?">
        <Radio checked label={reviewType} />
      </Input.Wrapper>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Input.Wrapper label="Received on">
          <DateDisplay format="eeee MM/dd/yy" value={receiptDate} />
        </Input.Wrapper>

        <Input.Wrapper label={reviewLabel}>
          <DateDisplay format="eeee MM/dd/yy" value={reviewDueDate} />
        </Input.Wrapper>

        <Input.Wrapper label={decisionLabel}>
          <DateDisplay format="eeee MM/dd/yy" value={decisionDueDate} />
        </Input.Wrapper>

        {sdr.status !== SDRStatus.DENIED && (
          <Input.Wrapper
            label={
              sdr.requestedFulfillmentDate
                ? 'Requested fulfillment by'
                : getEffectuationDateLabel(sdr)
            }
          >
            <Text>
              {sdr.requestedFulfillmentDate ? (
                <DateDisplay
                  format="eeee MM/dd/yy"
                  value={sdr.requestedFulfillmentDate}
                />
              ) : (
                <DateDisplay format="eeee MM/dd/yy" value={effectuationDate} />
              )}
            </Text>
          </Input.Wrapper>
        )}
      </SimpleGrid>
    </Stack>
  )
}
